// CustomModal.tsx
import { useEffect, useId, useRef } from 'react';
import '../css/CustomModal.css';
import { IconClose } from './ui';

interface CustomModalProps {
    visible: boolean;
    onDismiss: () => void;
    header: string;
    subHeader?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'default' | 'large' | 'xlarge';
    /** Extra class names for the modal body (e.g. form-builder layout). */
    bodyClassName?: string;
    /** When true, focus moves into the modal body on open and the close button is removed from tab order. */
    preferBodyFocus?: boolean;
    /** Optional id of an element that describes the dialog (aria-describedby). */
    describedById?: string;
    /** Extra class on the overlay (e.g. higher z-index). */
    overlayClassName?: string;
    /** Extra class on the dialog panel (e.g. xlarge launch layout). */
    contentClassName?: string;
    /** When false, a click on the dimmed backdrop does not close the dialog. */
    dismissOnBackdrop?: boolean;
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MODAL_OPEN_COUNT_ATTR = 'data-custom-modal-open-count';
const MODAL_OPEN_CLASS = 'custom-modal-open';

function lockPageScroll() {
    const root = document.documentElement;
    const count = Number(root.getAttribute(MODAL_OPEN_COUNT_ATTR) || '0') + 1;
    root.setAttribute(MODAL_OPEN_COUNT_ATTR, String(count));
    root.classList.add(MODAL_OPEN_CLASS);
}

function unlockPageScroll() {
    const root = document.documentElement;
    const next = Math.max(0, Number(root.getAttribute(MODAL_OPEN_COUNT_ATTR) || '1') - 1);
    if (next === 0) {
        root.removeAttribute(MODAL_OPEN_COUNT_ATTR);
        root.classList.remove(MODAL_OPEN_CLASS);
        return;
    }
    root.setAttribute(MODAL_OPEN_COUNT_ATTR, String(next));
}

export const CustomModal = ({
    visible,
    onDismiss,
    header,
    subHeader,
    children,
    footer,
    size = 'default',
    bodyClassName,
    preferBodyFocus = false,
    describedById,
    overlayClassName,
    contentClassName,
    dismissOnBackdrop = true,
}: CustomModalProps) => {
    const bodyRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const onDismissRef = useRef(onDismiss);
    /** Only dismiss when press + release both land on the backdrop (not after drag-select from a field). */
    const dismissOnClickRef = useRef(false);
    const titleId = useId();

    onDismissRef.current = onDismiss;

    useEffect(() => {
        if (!visible) return;

        lockPageScroll();
        return () => {
            unlockPageScroll();
        };
    }, [visible]);

    useEffect(() => {
        if (!visible) return;

        previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

        const timer = window.setTimeout(() => {
            if (preferBodyFocus) {
                const firstInBody = bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
                (firstInBody ?? dialogRef.current)?.focus();
                return;
            }
            const firstInDialog = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            (firstInDialog ?? dialogRef.current)?.focus();
        }, 0);

        const isTopmostModal = () => {
            const overlays = document.querySelectorAll('.custom-modal-overlay');
            if (overlays.length === 0) return false;
            return overlays[overlays.length - 1] === dialogRef.current?.parentElement;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            // Only the topmost open modal handles Escape / Tab trap (nested confirms).
            if (!isTopmostModal()) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onDismissRef.current();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) return;

            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
                (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
            );

            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement | null;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('keydown', onKeyDown, true);
            previouslyFocusedRef.current?.focus?.();
        };
    }, [visible, preferBodyFocus]);

    if (!visible) return null;

    return (
        <div
            className={['custom-modal-overlay', overlayClassName].filter(Boolean).join(' ')}
            onMouseDown={(e) => {
                if (!dismissOnBackdrop) return;
                dismissOnClickRef.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
                if (dismissOnBackdrop && dismissOnClickRef.current && e.target === e.currentTarget) {
                    onDismiss();
                }
                dismissOnClickRef.current = false;
            }}
        >
            <div
                ref={dialogRef}
                className={[
                    'custom-modal-content',
                    size === 'large' ? 'custom-modal-large' : '',
                    size === 'xlarge' ? 'custom-modal-xlarge' : '',
                    contentClassName,
                ]
                    .filter(Boolean)
                    .join(' ')}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={describedById}
                tabIndex={-1}
            >
                <div className="custom-modal-header">
                    <div className="custom-modal-header-heading">
                        <h2 id={titleId}>{header}</h2>
                        <button
                            type="button"
                            title="dismiss"
                            aria-label="Close"
                            className="custom-modal-close"
                            tabIndex={preferBodyFocus ? -1 : 0}
                            onClick={(event) => {
                                event.stopPropagation();
                                onDismiss();
                            }}
                        >
                            <IconClose iconClasses="custom-modal-close-icon" height={12} width={12} />
                        </button>
                    </div>
                    {subHeader && <div className="custom-modal-subheader">{subHeader}</div>}
                </div>
                <div className={['custom-modal-body', bodyClassName].filter(Boolean).join(' ')} ref={bodyRef}>
                    {children}
                </div>
                {footer && <div className="custom-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};
